import { message } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/Auth'

interface PropertyFormData {
  propertyType: string
  unitSize: string
  bedrooms: string
  bathrooms: string
  floorNumber: string
  roomNumber: string
  roomPosition: string
  hasBalcony: boolean | null
  isPetFriendly: boolean | null
  maxGuests: string
  availability: string
  availableDate: any
  propertyPhotos: string[]
  videoTour: string | null
  floorPlan: string | null
  monthlyPrice: string
  sixMonthPrice: string
  yearlyPrice: string
  securityDeposit: string
  useHaupAgent: boolean
}

interface Step2Props {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: PropertyFormData
  updateFormData: (data: Partial<PropertyFormData>) => void
  isSubmittingDraft?: boolean
}

const Step2: React.FC<Step2Props> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
  isSubmittingDraft = false,
}) => {
  const [progress, setProgress] = useState(0)
  const [uploadingField, setUploadingField] = useState<string | null>(null)
  const [dragOverField, setDragOverField] = useState<string | null>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const floorPlanInputRef = useRef<HTMLInputElement>(null)
  const { userLoggedIn } = useAuth()

  const calculateProgress = () => {
    const fields = [formData.propertyPhotos.length > 0]

    const filledFields = fields.filter(Boolean).length
    const progressPercentage = Math.round((filledFields / fields.length) * 100)
    return progressPercentage
  }

  useEffect(() => {
    const newProgress = calculateProgress()
    setProgress(newProgress)
    onStepProgress(newProgress)
  }, [formData, onStepProgress])

  const handleFileUpload = async (
    type: 'photo' | 'video' | 'floorPlan',
    files: FileList | null,
  ) => {
    if (!files || files.length === 0) return

    if (!userLoggedIn?.userToken) {
      message.error('Authentication token is missing. Please log in again.')
      return
    }

    const allowedImageTypes = ['image/jpeg', 'image/png']
    const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo']
    const maxImageSize = 20 * 1024 * 1024
    const maxVideoSize = 100 * 1024 * 1024
    const maxPhotos = 7

    if (type === 'photo') {
      const totalPhotosAfterUpload = formData.propertyPhotos.length + files.length
      if (totalPhotosAfterUpload > maxPhotos) {
        message.error(
          `You can upload a maximum of ${maxPhotos} photos. You currently have ${formData.propertyPhotos.length} photos.`,
        )
        return
      }
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const isValidType =
        type === 'video'
          ? allowedVideoTypes.includes(file.type)
          : allowedImageTypes.includes(file.type)

      if (!isValidType) {
        const expectedTypes = type === 'video' ? 'MP4, MOV, or AVI' : 'JPEG or PNG'
        message.error(
          `Invalid file type for ${file.name}. Please upload ${expectedTypes} files only.`,
        )
        return
      }

      const maxAllowedSize = type === 'video' ? maxVideoSize : maxImageSize
      const sizeLimit = type === 'video' ? '100MB' : '20MB'

      if (file.size > maxAllowedSize) {
        message.error(`File ${file.name} exceeds the ${sizeLimit} limit.`)
        return
      }
    }

    setUploadingField(type)

    try {
      if (type === 'photo') {
        const uploadPromises = Array.from(files).map(async (file) => {
          const formDataUpload = new FormData()
          formDataUpload.append('fileName', file)
          formDataUpload.append('deviceType', 'WEBSITE')
          formDataUpload.append('project', 'cms')

          const response = await fetch(`${process.env.REACT_APP_TS_UPLOAD_URL}/upload`, {
            method: 'POST',
            headers: {
              Authorization: userLoggedIn.userToken,
              project: 'haup-stable',
            },
            body: formDataUpload,
          })

          if (!response.ok) {
            throw new Error(`Upload failed for ${file.name}`)
          }

          const result = await response.json()
          if (result.result && result.fileUrl) {
            return result.fileUrl
          } else {
            throw new Error(result.message || `Upload failed for ${file.name}`)
          }
        })

        const uploadedUrls = await Promise.all(uploadPromises)
        updateFormData({
          propertyPhotos: [...formData.propertyPhotos, ...uploadedUrls],
        })
        message.success(`${uploadedUrls.length} photo(s) uploaded successfully!`)
      } else {
        const file = files[0]
        const formDataUpload = new FormData()
        formDataUpload.append('fileName', file)
        formDataUpload.append('deviceType', 'WEBSITE')
        formDataUpload.append('project', 'cms')

        const uploadEndpoint = type === 'video' ? '/upload/upload-video' : '/upload'
        const response = await fetch(`${process.env.REACT_APP_TS_UPLOAD_URL}${uploadEndpoint}`, {
          method: 'POST',
          headers: {
            Authorization: userLoggedIn.userToken,
            project: 'haup-stable',
          },
          body: formDataUpload,
        })

        if (!response.ok) {
          throw new Error(`Upload failed for ${file.name}`)
        }

        const result = await response.json()
        if (result.result && result.fileUrl) {
          if (type === 'video') {
            updateFormData({ videoTour: result.fileUrl })
            message.success('Video uploaded successfully!')
          } else if (type === 'floorPlan') {
            updateFormData({ floorPlan: result.fileUrl })
            message.success('Floor plan uploaded successfully!')
          }
        } else {
          throw new Error(result.message || `Upload failed for ${file.name}`)
        }
      }
    } catch (err: any) {
      message.error(err.message || 'An error occurred during upload')
    } finally {
      setUploadingField(null)
    }
  }

  const removePhoto = (index: number) => {
    const newPhotos = formData.propertyPhotos.filter((_, i) => i !== index)
    updateFormData({ propertyPhotos: newPhotos })
  }

  const removeVideo = () => {
    updateFormData({ videoTour: null })
  }

  const removeFloorPlan = () => {
    updateFormData({ floorPlan: null })
  }

  const handleDragOver = (e: React.DragEvent, field: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverField(field)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverField(null)
    }
  }

  const handleDrop = (e: React.DragEvent, field: 'photo' | 'video' | 'floorPlan') => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverField(null)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      handleFileUpload(field, files)
    }
  }

  const isNextDisabled = progress < 100 || isSubmittingDraft

  return (
    <div
      style={{
        backgroundColor: '#fff',
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <div style={{ padding: '24px 16px' }}>
        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '12px',
              fontWeight: '400',
            }}
          >
            Photos of your property
          </label>

          {formData.propertyPhotos.length < 7 && (
            <div
              onClick={() => !uploadingField && photoInputRef.current?.click()}
              onDragOver={(e) => handleDragOver(e, 'photo')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'photo')}
              style={{
                border: `2px dashed ${dragOverField === 'photo' ? '#4C4848' : '#D6DAE0'}`,
                borderRadius: '8px',
                padding: '32px 16px',
                textAlign: 'center',
                cursor: uploadingField === 'photo' ? 'not-allowed' : 'pointer',
                backgroundColor:
                  dragOverField === 'photo'
                    ? '#FFF8F3'
                    : uploadingField === 'photo'
                    ? '#f5f5f5'
                    : '#FAFAFA',
                marginBottom: '16px',
                opacity: uploadingField === 'photo' ? 0.6 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ marginBottom: '12px' }}>
                <img
                  src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1CucRaQpqsqxAWv2pcxRk14zzSj7ec1akHuLJRnuZSaVKt5gvm4hPYRv1aL2hbP2PEjkEqsjkTKzjxJoN2s6azQn.png"
                  alt="Upload"
                  style={{ width: '40px', height: '40px' }}
                />
              </div>
              <p
                style={{
                  color: uploadingField === 'photo' ? '#999' : '#4C4848',
                  fontSize: '14px',
                  margin: '0',
                  fontWeight: '500',
                }}
              >
                {uploadingField === 'photo'
                  ? 'Uploading...'
                  : dragOverField === 'photo'
                  ? 'Drop photos here'
                  : 'Upload, take a photo or drag & drop'}
              </p>
            </div>
          )}

          {formData.propertyPhotos.length >= 7 && (
            <div
              style={{
                border: '2px solid #22c55e',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                backgroundColor: '#f0fdf4',
                marginBottom: '16px',
              }}
            >
              <p
                style={{
                  color: '#16a34a',
                  fontSize: '14px',
                  margin: '0',
                  fontWeight: '500',
                }}
              >
                ✓ Maximum photos uploaded (7/7)
              </p>
            </div>
          )}

          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              handleFileUpload('photo', e.target.files)
              e.target.value = ''
            }}
            style={{ display: 'none' }}
          />

          {formData.propertyPhotos.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                gap: '8px',
                marginTop: '16px',
              }}
            >
              {formData.propertyPhotos.map((photo, index) => (
                <div
                  key={index}
                  style={{
                    position: 'relative',
                    aspectRatio: '1',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={photo}
                    alt={`Property ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removePhoto(index)
                    }}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '4px',
              fontWeight: '400',
            }}
          >
            Video virtual tour <span style={{ color: '#999', fontSize: '12px' }}>(optional)</span>
          </label>
          <p
            style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '12px',
              margin: '0 0 12px 0',
            }}
          >
            Add a video or virtual tour to showcase your property in 360°.
          </p>

          {!formData.videoTour ? (
            <div
              onClick={() => !uploadingField && videoInputRef.current?.click()}
              onDragOver={(e) => handleDragOver(e, 'video')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'video')}
              style={{
                border: `2px dashed ${dragOverField === 'video' ? '#4C4848' : '#D6DAE0'}`,
                borderRadius: '8px',
                padding: '32px 16px',
                textAlign: 'center',
                cursor: uploadingField === 'video' ? 'not-allowed' : 'pointer',
                backgroundColor:
                  dragOverField === 'video'
                    ? '#FFF8F3'
                    : uploadingField === 'video'
                    ? '#f5f5f5'
                    : '#FAFAFA',
                opacity: uploadingField === 'video' ? 0.6 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ marginBottom: '12px' }}>
                <img
                  src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1CucRaQpqsqxAWv2pcxRk14zzSj7ec1akHuLJRnuZSaVKt5gvm4hPYRv1aL2hbP2PEjkEqsjkTKzjxJoN2s6azQn.png"
                  alt="Upload"
                  style={{ width: '40px', height: '40px' }}
                />
              </div>
              <p
                style={{
                  color: uploadingField === 'video' ? '#999' : '#4C4848',
                  fontSize: '14px',
                  margin: '0',
                  fontWeight: '500',
                }}
              >
                {uploadingField === 'video'
                  ? 'Uploading...'
                  : dragOverField === 'video'
                  ? 'Drop video here'
                  : 'Upload, take a video or drag & drop'}
              </p>
            </div>
          ) : (
            <div
              style={{
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0',
                aspectRatio: '16/9',
              }}
            >
              <video
                src={formData.videoTour}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                controls
              />
              <button
                onClick={removeVideo}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ×
              </button>
            </div>
          )}

          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={(e) => {
              handleFileUpload('video', e.target.files)
              e.target.value = ''
            }}
            style={{ display: 'none' }}
          />
        </div>

        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              color: '#333',
              marginBottom: '4px',
              fontWeight: '400',
            }}
          >
            Floor plan <span style={{ color: '#999', fontSize: '12px' }}>(optional)</span>
          </label>
          <p
            style={{
              fontSize: '12px',
              color: '#666',
              marginBottom: '12px',
              margin: '0 0 12px 0',
            }}
          >
            Add a floor plan image to help guests understand the property layout.
          </p>

          {!formData.floorPlan ? (
            <div
              onClick={() => !uploadingField && floorPlanInputRef.current?.click()}
              onDragOver={(e) => handleDragOver(e, 'floorPlan')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'floorPlan')}
              style={{
                border: `2px dashed ${dragOverField === 'floorPlan' ? '#4C4848' : '#D6DAE0'}`,
                borderRadius: '8px',
                padding: '32px 16px',
                textAlign: 'center',
                cursor: uploadingField === 'floorPlan' ? 'not-allowed' : 'pointer',
                backgroundColor:
                  dragOverField === 'floorPlan'
                    ? '#FFF8F3'
                    : uploadingField === 'floorPlan'
                    ? '#f5f5f5'
                    : '#FAFAFA',
                opacity: uploadingField === 'floorPlan' ? 0.6 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ marginBottom: '12px' }}>
                <img
                  src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1CucRaQpqsqxAWv2pcxRk14zzSj7ec1akHuLJRnuZSaVKt5gvm4hPYRv1aL2hbP2PEjkEqsjkTKzjxJoN2s6azQn.png"
                  alt="Upload"
                  style={{ width: '40px', height: '40px' }}
                />
              </div>
              <p
                style={{
                  color: uploadingField === 'floorPlan' ? '#999' : '#4C4848',
                  fontSize: '14px',
                  margin: '0',
                  fontWeight: '500',
                }}
              >
                {uploadingField === 'floorPlan'
                  ? 'Uploading...'
                  : dragOverField === 'floorPlan'
                  ? 'Drop floor plan here'
                  : 'Upload, take a photo or drag & drop'}
              </p>
            </div>
          ) : (
            <div
              style={{
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0',
              }}
            >
              <img
                src={formData.floorPlan}
                alt="Floor plan"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
              <button
                onClick={removeFloorPlan}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ×
              </button>
            </div>
          )}

          <input
            ref={floorPlanInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              handleFileUpload('floorPlan', e.target.files)
              e.target.value = ''
            }}
            style={{ display: 'none' }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <button
            onClick={onPrevious}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: '#666',
              border: '1px solid #e0e0e0',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Back
          </button>
          <button
            onClick={onNext}
            disabled={isNextDisabled}
            style={{
              flex: '1',
              padding: '12px 24px',
              backgroundColor: isNextDisabled ? '#ccc' : '#4C4848',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              cursor: isNextDisabled ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '16px',
              fontFamily: 'Poppins, sans-serif',
              opacity: isNextDisabled ? 0.6 : 1,
            }}
          >
            {isSubmittingDraft ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Step2
